import { useFrame, useThree } from '@react-three/fiber'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  CameraCoords,
  ExtrudingFeature,
  MarkupPrimitiveMeshData,
  MouseState,
  Vec2,
} from 'features/assetViz/utils/helpers'
import { RenderLayerID } from 'features/assetViz/utils/renderingDefines'
import React, { useMemo } from 'react'
import { Intersection, MathUtils, Vector2 } from 'three'
import {
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentRect,
  GeckoLegacyBoilerWall,
  GeckoMarkupPrimitive,
  GeckoPlate,
  GeckoShellCourse,
} from 'types'

type MouseClick = MouseState & {
  cameraStart: CameraCoords | undefined
}

/**
 * An invisible, optional, intermediary layer containing logic pertaining to
 * mouse movement, clicks, etc. within AssetViz.
 * @param cameraCoords          The camera's current coordinates, used to calculate
 *                              camera movement based on mouse movement. If undefined, MouseHandler
 *                               will not move the camera.
 * @param getMovementForCursorDelta A function which takes a Vec2 representing the
 *                                  mouse's movement since the last frame and returns a Vec2.
 *                                  If undefined, MouseHandler will not move the camera.
 */
export const MouseHandler: React.FC<{
  cameraCoords?: CameraCoords
  getMovementForCursorDelta?: (mouseMovement: Vec2) => Vec2
}> = ({ cameraCoords, getMovementForCursorDelta }) => {
  //----------------------------------------------------------------------------

  const {
    userIsNavigating,
    setUserIsNavigating,
    onClicked,
    onHovered,
    overrideCameraAspectRatio,
  } = useAssetVizInternal()

  const [isOverCanvas, setIsOverCanvas] = React.useState(false)
  const [curClick, setCurClick] = React.useState<Readonly<MouseClick>>()

  // Use a ref for the mouse's current state to avoid per-frame reallocations
  const mouseStateRef = React.useRef<MouseState>({
    mouseX: NaN,
    mouseY: NaN,
    positionX: NaN,
    positionY: NaN,
    positionZ: NaN,
    componentX: NaN,
    componentY: NaN,
    componentWrapsX: false,
    componentWrapsY: false,
    componentBounds: undefined,
    componentModel: undefined,
    componentDataFrame: undefined,
    plate: undefined,
    course: undefined,
    wall: undefined,
    extrudingFeature: undefined,
    markupPrimitive: undefined,
  })

  // Mouse & trackpad interactions ---------------------------------------------

  const onMouseDown = React.useCallback(
    (event: MouseEvent) => {
      if (event.button !== 0) return // Left click only!

      // Prevent any other components from handling this click, preventing
      // unintended text selection, etc.
      event.preventDefault()

      // Also save the mouse coordinates on mouse down so touchscreens have
      // the correct point of reference for cursor delta on move events
      mouseStateRef.current.mouseX = event.clientX
      mouseStateRef.current.mouseY = event.clientY

      // Save the mouse's and camera's positions to the "curClick" object
      setCurClick({
        ...mouseStateRef.current,
        cameraStart: cameraCoords ? { ...cameraCoords } : undefined,
      })

      // The user isn't "navigating" until they move the mouse
      setUserIsNavigating(false)
    },
    [cameraCoords, mouseStateRef, setUserIsNavigating]
  )

  const onMouseUp = React.useCallback(() => {
    // If the user releases their mouse button while in the midst of a click
    // action *other* than navigation (click-and-drag) then fire a callback
    if (!!curClick && !userIsNavigating) onClicked(curClick)

    // Reset the current click
    setCurClick(undefined)

    // The user isn't "navigating" if they release the mouse
    setUserIsNavigating(false)
  }, [curClick, userIsNavigating, onClicked, setUserIsNavigating])

  const onMouseEnter = React.useCallback(() => {
    setIsOverCanvas(true)
  }, [])

  const onMouseLeave = React.useCallback(() => {
    setIsOverCanvas(false)
    onHovered(undefined)
  }, [onHovered])

  const onMouseMove = React.useCallback(
    (event: MouseEvent) => {
      if (pinchDistanceRef.current !== -1) return // mid-pinch
      setIsOverCanvas(true)

      mouseStateRef.current.mouseX = event.clientX
      mouseStateRef.current.mouseY = event.clientY

      // The user can be considered navigating if they're clicking and have
      // moved from their initial click position
      setUserIsNavigating(
        !!curClick &&
          (Math.abs(curClick.mouseX - mouseStateRef.current.mouseX) > 10 ||
            Math.abs(curClick.mouseY - mouseStateRef.current.mouseY) > 10)
      )
      onHovered(mouseStateRef.current)
    },
    [curClick, mouseStateRef, setUserIsNavigating, onHovered]
  )

  const onWheel = React.useCallback(
    (event: WheelEvent) => {
      onHovered(undefined) // Drops the tooltip when scrolling
      event.preventDefault() // Prevents pinch/wheel movement from moving the whole app
      if (cameraCoords)
        cameraCoords.distToComponent +=
          0.005 * event.deltaY * cameraCoords.distToComponent
    },
    [cameraCoords, onHovered]
  )

  const pinchDistanceRef = React.useRef<number>(-1)
  const onTouchMove = React.useCallback(
    (event: TouchEvent) => {
      if (event.changedTouches.length === 2) {
        onHovered(undefined) // Hide tooltip when pinching
        event.preventDefault() // Prevent mousemove handler from panning
        const touch1 = event.changedTouches[0]
        const touch2 = event.changedTouches[1]
        const newPinchDistance = new Vector2(
          touch1.clientX,
          touch1.clientY
        ).distanceTo(new Vector2(touch2.clientX, touch2.clientY))
        if (pinchDistanceRef.current !== -1 && cameraCoords) {
          cameraCoords.distToComponent -=
            0.005 *
            (newPinchDistance - pinchDistanceRef.current) *
            cameraCoords.distToComponent
        }
        pinchDistanceRef.current = newPinchDistance
      }
    },
    [cameraCoords, onHovered]
  )

  const onTouchEnd = React.useCallback(() => {
    pinchDistanceRef.current = -1
  }, [])

  //--------------------------------------------------------------------------

  const { gl } = useThree()

  React.useEffect(() => {
    const ref = gl.domElement
    ref.addEventListener('wheel', onWheel, { passive: false })
    ref.addEventListener('pointerdown', onMouseDown, { passive: false })
    ref.addEventListener('pointerenter', onMouseEnter, { passive: false })
    ref.addEventListener('pointerleave', onMouseLeave, { passive: false })
    ref.addEventListener('pointermove', onMouseMove)
    ref.addEventListener('touchmove', onTouchMove)
    ref.addEventListener('touchend', onTouchEnd)
    return () => {
      ref.removeEventListener('wheel', onWheel)
      ref.removeEventListener('pointerdown', onMouseDown)
      ref.removeEventListener('pointerenter', onMouseEnter)
      ref.removeEventListener('pointerleave', onMouseLeave)
      ref.removeEventListener('pointermove', onMouseMove)
      ref.removeEventListener('touchmove', onTouchMove)
      ref.removeEventListener('touchend', onTouchEnd)
    }
  }, [
    gl,
    onMouseDown,
    onMouseMove,
    onMouseEnter,
    onMouseLeave,
    onWheel,
    onTouchMove,
    onTouchEnd,
  ])

  React.useEffect(() => {
    document.addEventListener('pointerup', onMouseUp)
    return () => document.removeEventListener('pointerup', onMouseUp)
  }, [onMouseUp])

  //----------------------------------------------------------------------------

  // To avoid array allocation every frame, these are allocated once and reused
  const intersectionsArray = useMemo(() => new Array<Intersection>(10), [])
  const cursorDeltaArray = useMemo<Vec2>(() => [0, 0], [])

  useFrame(({ scene, raycaster, pointer, camera }) => {
    if (!isOverCanvas) return

    // If the mouse is over this component, see if it's over anything
    raycaster.setFromCamera(pointer, camera)
    raycaster.layers.enable(RenderLayerID.VisibleCollidable)
    raycaster.layers.enable(RenderLayerID.InvisibleCollidable)

    // Clear out the intersections array and populate it in-place
    intersectionsArray.length = 0
    raycaster.intersectObjects(scene.children, true, intersectionsArray)

    // Reset the mouseStateRef object in-place
    mouseStateRef.current.positionX = NaN
    mouseStateRef.current.positionY = NaN
    mouseStateRef.current.positionZ = NaN
    mouseStateRef.current.componentX = NaN
    mouseStateRef.current.componentY = NaN
    mouseStateRef.current.componentWrapsX = false
    mouseStateRef.current.componentWrapsY = false
    mouseStateRef.current.componentBounds = undefined
    mouseStateRef.current.componentDataFrame = undefined
    mouseStateRef.current.componentModel = undefined
    mouseStateRef.current.plate = undefined
    mouseStateRef.current.course = undefined
    mouseStateRef.current.wall = undefined
    mouseStateRef.current.extrudingFeature = undefined
    mouseStateRef.current.markupPrimitive = undefined

    // Use a for loop for efficiency regions, looping over each raycast hit
    for (const hit of intersectionsArray) {
      const bounds = hit.object.userData.bounds as GeckoComponentRect
      const componentModel = hit.object.userData
        .componentModel as GeckoComponentModel
      if (!mouseStateRef.current.componentModel && componentModel)
        mouseStateRef.current.componentModel = componentModel
      if (!mouseStateRef.current.componentBounds && hit.uv && bounds) {
        mouseStateRef.current.positionX = hit.point.x
        mouseStateRef.current.positionY = hit.point.y
        mouseStateRef.current.positionZ = hit.point.z
        mouseStateRef.current.componentX = MathUtils.lerp(
          bounds.start.x.num,
          bounds.end.x.num,
          hit.uv.x
        )
        mouseStateRef.current.componentY = MathUtils.lerp(
          bounds.start.y.num,
          bounds.end.y.num,
          hit.uv.y
        )
        mouseStateRef.current.componentBounds = bounds
        mouseStateRef.current.componentWrapsX = Boolean(
          hit.object.userData.doWrapX
        )
        mouseStateRef.current.componentWrapsY = Boolean(
          hit.object.userData.doWrapY
        )
      }
      mouseStateRef.current.componentDataFrame ??= hit.object?.userData.cdf as
        | GeckoComponentDataFrame
        | undefined
      mouseStateRef.current.course ??= hit.object?.userData?.course as
        | GeckoShellCourse
        | undefined
      mouseStateRef.current.wall ??= hit.object?.userData?.wall as
        | GeckoLegacyBoilerWall
        | undefined
      mouseStateRef.current.extrudingFeature ??= hit.object?.userData
        ?.extrudingFeature as ExtrudingFeature | undefined
      const markupPrimitiveHit = hit.object?.userData?.markupPrimitives as
        | MarkupPrimitiveMeshData
        | undefined

      if (
        !mouseStateRef.current.markupPrimitive &&
        markupPrimitiveHit &&
        hit.faceIndex
      ) {
        const primitives =
          markupPrimitiveHit.primitives as GeckoMarkupPrimitive[]
        const indexes = markupPrimitiveHit.indexes as number[]
        const faceIndexHit = hit.faceIndex
        for (let i = 0; i < indexes.length; i++) {
          if (faceIndexHit < indexes[i]) {
            mouseStateRef.current.markupPrimitive = primitives[i]
            break
          }
        }
      }

      // Multiple plates might overlap, so we should select the one with the
      // newest installation date (rather than just the first hit by raycast)
      const plate = hit.object?.userData?.plate as GeckoPlate | undefined
      if (
        // If this is the first plate this raycast hits
        !mouseStateRef.current.plate ||
        // If the raycast hits a plate with an installation date, prefer it
        (!mouseStateRef.current.plate.installationDate &&
          !plate?.installationDate) ||
        // If the raycast hits a plate with a newer installation date than
        // other plates hit with this raycast
        (!!mouseStateRef.current.plate.installationDate &&
          !!plate?.installationDate &&
          mouseStateRef.current.plate.installationDate.getDate() >
            plate.installationDate.getDate())
      ) {
        mouseStateRef.current.plate = plate
      }
    }

    // If single-clicking, move the camera around
    if (cameraCoords && curClick?.cameraStart && getMovementForCursorDelta) {
      const movementSpeed = 0.001 * cameraCoords.distToComponent
      let movementVectorX = movementSpeed
      let movementVectorY = movementSpeed
      if (overrideCameraAspectRatio && overrideCameraAspectRatio.current > 0) {
        if (overrideCameraAspectRatio.current > 1) {
          movementVectorX *= overrideCameraAspectRatio.current
        } else {
          movementVectorY /= overrideCameraAspectRatio.current
        }
      }
      cursorDeltaArray[0] = mouseStateRef.current.mouseX - curClick.mouseX
      cursorDeltaArray[1] = mouseStateRef.current.mouseY - curClick.mouseY
      const deltaXY = getMovementForCursorDelta(cursorDeltaArray)
      cameraCoords.componentX =
        curClick.cameraStart.componentX - movementVectorX * deltaXY[0]
      cameraCoords.componentY =
        curClick.cameraStart.componentY + movementVectorY * deltaXY[1]
    }
  })

  //----------------------------------------------------------------------------

  return <></>
}
